<template>
  <div class="page-content">
    <!-- Loading state -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mx-auto"></div>
      <p class="mt-4">{{ $t('loading', 'Loading...') }}</p>
    </div>
    
    <!-- Content when loaded -->
    <div v-else-if="pageContent" class="space-y-6">
      <!-- Render content blocks -->
      <div 
        v-for="(block, index) in pageContent.blocks" 
        :key="index"
        class="content-block"
      >
        <!-- Text Block -->
        <TextBlock 
          v-if="block.type === 'text'" 
          :block="block" 
        />
        
        <!-- Photo Block -->
        <PhotoBlock 
          v-if="block.type === 'photo'" 
          :block="block" 
        />
        
        <!-- Video Block -->
        <VideoBlock 
          v-if="block.type === 'video'" 
          :block="block" 
        />
        
        <!-- Audio Block -->
        <AudioBlock 
          v-if="block.type === 'audio'" 
          :block="block" 
        />

        <!-- Audio Sync Block -->
        <AudioSyncBlock 
          v-if="block.type === 'audiosync'" 
          :block="block" 
        />
        
        <!-- Link Block -->
        <LinkBlock 
          v-if="block.type === 'link'" 
          :block="block" 
        />

        <!-- Connectbox Block -->
        <ConnectboxBlock 
          v-if="block.type === 'connectbox'" 
          :block="block" 
        />

        <!-- Quiz Question Block -->
        <FormRadioInput
          v-if="block.type === 'form_radio_input'"
          :data="block.data"
          :block="block"
          :form="form"
          :getQuizAnswer="getQuizAnswer"
          :handleChangeElement="handleChangeElement"
          :disabled="isPageLocked"
          @change="handleChangeElement"
        />
        
        <!-- Text Input Block -->
        <FormTextInput
          v-if="block.type === 'form_text_input'"
          :name="`answer_${block.data?.id || 'unknown'}`"
          :value="getTextAnswer(block.data?.id)"
          :disabled="isPageLocked"
          :questionTitle="block.data?.question"
          :placeholder="block.data?.placeholder"
          @change="handleChangeElement"
        />

        <!-- TextArea Block -->
        <FormTextarea
          v-if="block.type === 'form_textarea'" 
          :name="`answer_${block.data?.id || 'unknown'}`"
          :value="getTextAnswer(block.data?.id)"
          :disabled="isPageLocked"
          :questionTitle="block.data?.question"
          :rows="block.data?.rows || 3"
          :placeholder="block.data?.placeholder"
          @change="handleChangeElement"
        />
        
        <!-- Variable Input -->
        <FormVariableInput
          v-if="block.type === 'form_variable_input'" 
          :name="`variable_${block.data?.variable_slug || block.data?.variable || 'unknown'}`"
          :value="getVariableValueFromForm(block.data?.variable_slug || block.data?.variable)"
          :disabled="isPageLocked"
          :questionTitle="block.data?.question"
          :variableTitle="block.data?.title"
          :variableDescription="block.data?.description"
          :inputType="block.data?.type || 'text'"
          :placeholder="block.data?.placeholder"
          :min="block.data?.min"
          :max="block.data?.max"
          :options="block.data?.options || []"
          @change="handleChangeElement"
        />
        
        <!-- Form Submit Button -->
        <FormSubmitButton 
          v-if="block.type === 'form_submit_button'" 
          :block="block"
          :form="form"
          :disabled="!isPageComplete || isPageLocked"
          :isPageLocked="isPageLocked"
          :message="currentMessage"
          @submit="handleFormSubmit"
        />

        <GameJigsawPuzzleBlock
          v-if="block.type === 'game_jigsaw_puzzle'"
          :ref="`game-${block.id || block.type}`"
          @complete="handleJigsawComplete"
          :block="block"
          :isCompleted="getGameCompletionStatus(block)"
        />

        <GameImageSpotter
          v-if="block.type === 'game_image_spotter'"
          :ref="`game-${block.id || block.type}`"
          @complete="handleSpotterComplete"
          :block="block"
          :gameId="getGameId(block)"
          :isCompleted="getGameCompletionStatus(block)"
        />

        <GameImageEraser
          v-if="block.type === 'game_image_eraser'"
          :ref="`game-${block.id || block.type}`"
          @complete="handleEraserComplete"
          :block="block"
          :isCompleted="getGameCompletionStatus(block)"
        />

        <GameImageSweeper
          v-if="block.type === 'game_image_sweeper'"
          :ref="`game-${block.id || block.type}`"
          @complete="handleSweeperComplete"
          :block="block"
          :isCompleted="getGameCompletionStatus(block)"
        />

      </div>
      
      <!-- Submit button for pages without explicit submit button as fallback to have a working form -->
      <FormSubmitButton 
        v-if="hasFormFields && !hasSubmitBlock"
        :form="form"
        :disabled="!isPageComplete || isPageLocked"
        :isPageLocked="isPageLocked"
        :message="currentMessage"
        :buttonText="$t('button.send')"
        @submit="handleFormSubmit"
      />
    </div>
    
    <!-- No content message -->
    <div v-else class="text-center py-8 text-gray-500">
      <p>{{ $t('page.no_content', 'No content available for this page') }}</p>
    </div>
  </div>
</template>

<script>
import TextBlock from './TextBlock.vue'
import PhotoBlock from './PhotoBlock.vue'
import VideoBlock from './VideoBlock.vue'
import AudioBlock from './AudioBlock.vue'
import AudioSyncBlock from './AudioSyncBlock.vue'
import LinkBlock from './LinkBlock.vue'
import ConnectboxBlock from './ConnectboxBlock.vue'
import FormRadioInput from './form/FormRadioInput.vue'
import FormTextInput from './form/FormTextInput.vue'
import FormTextarea from './form/FormTextarea.vue'
import FormVariableInput from './form/FormVariableInput.vue'
import FormSubmitButton from './form/FormSubmitButton.vue'
import api from '../services/api'
import gameModule from '../modules/GameModule.js'
import moduleManager from '../modules/index.js'
import GameJigsawPuzzleBlock from "./GameJigsawPuzzleBlock.vue";
import GameImageSpotter from "./GameImageSpotter.vue";
import GameImageEraser from "./GameImageEraser.vue";
import GameImageSweeper from "./GameImageSweeper.vue";

export default {
  name: 'PageContent',
  components: {
    GameImageSweeper,
    GameImageEraser,
    GameImageSpotter,
    GameJigsawPuzzleBlock,
    TextBlock,
    PhotoBlock,
    VideoBlock,
    AudioBlock,
    AudioSyncBlock,
    LinkBlock,
    ConnectboxBlock,
    FormRadioInput,
    FormTextInput,
    FormTextarea,
    FormVariableInput,
    FormSubmitButton
  },
  props: {
    pageId: {
      type: [String, Number],
      required: true
    },
    tourId: {
      type: [String, Number],
      default: null
    },
    form: {
      type: Object,
      default: () => ({})
    },
    getQuizAnswer: {
      type: Function,
      default: null
    },
    handleChangeElement: {
      type: Function,
      default: null
    },
    handleSubmit: {
      type: Function,
      default: null
    },
    isPageComplete: {
      type: Boolean,
      default: false
    },
    isPageLocked: {
      type: Boolean,
      default: false
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      pageContent: null,
      currentMessage: '',
      gameCompletions: {} // Track which games have been completed
    }
  },
  computed: {
    // Use provided tourId or fall back to activeTour from store
    currentTourId() {
      return this.tourId || null
    },
    hasFormFields() {
      const hasFields = this.pageContent && this.pageContent.blocks.some(block => 
        block.type && (block.type.startsWith('form_') || block.type.startsWith('game_')) && block.type !== 'form_submit_button'
      );
      console.log('PageContent: hasFormFields computed:', {
        hasPageContent: !!this.pageContent,
        blocksCount: this.pageContent?.blocks?.length || 0,
        blockTypes: this.pageContent?.blocks?.map(b => b.type) || [],
        hasFields
      });
      return hasFields;
    },
    hasSubmitBlock() {
      return this.pageContent && this.pageContent.blocks.some(block => block.type === 'form_submit_button');
    },
    
    // Check if current page form is complete
    localIsPageComplete() {
      if (!this.pageContent || !this.pageContent.blocks) {
        console.log('PageContent: No page content or blocks available')
        return false
      }
      
      const blocks = this.pageContent.blocks
      console.log('PageContent: Checking form completion for', blocks.length, 'blocks')
      
      for (const block of blocks) {
        if (block.type === 'form_radio_input') {
          const questionId = block.data?.id?.toString()
          if (questionId) {
            // Check if answer exists in form state
            const hasAnswer = this.form.quiz_answers && this.form.quiz_answers[questionId]
            console.log(`PageContent: Question ${questionId} answered: ${hasAnswer}`)
            if (!hasAnswer) {
              console.log(`PageContent: Form incomplete - quiz question ${questionId} not answered`)
              return false
            }
          }
        } else if (block.type === 'form_text_input') {
          const questionId = block.data?.id?.toString()
          if (questionId && block.data?.required) {
            // Check if answer exists in form state
            const hasAnswer = this.form.quiz_answers && this.form.quiz_answers[questionId] && this.form.quiz_answers[questionId].trim() !== ''
            console.log(`PageContent: Text question ${questionId} answered: ${hasAnswer}`)
            if (!hasAnswer) {
              console.log(`PageContent: Form incomplete - text question ${questionId} not answered`)
              return false
            }
          }
        } else if (block.type === 'form_variable_input') {
          const varSlug = block.data?.variable_slug || block.data?.variable // fallback for backward compatibility
          if (varSlug && block.data?.required) {
            // Check if answer exists in GameModule (persistent storage)
            const hasGameModuleAnswer = gameModule.getVariableValue(varSlug) !== null

            if (!hasGameModuleAnswer) {
              console.log(`PageContent: Form incomplete - required variable ${varSlug} not filled in GameModule`)
              return false
            }
          }
        } else if (block.type === 'game_image_spotter') {
          // Check if game has been completed
          const gameId = this.getGameId(block)
          console.log(`Form check: Game ${block.type} (${gameId}) form.game_answers:`, this.form.game_answers)
          const hasAnswer = this.form.game_answers && this.form.game_answers[gameId];

          console.log(`Form check: Game ${block.type} (${gameId}) hasAnswer: ${hasAnswer}`)
          if (!hasAnswer) {
            console.log(`Form incomplete: game ${block.type} not answered`)
            return false
          }
        } else if (this.isGameBlock(block.type)) {
          // Check if game has been completed
          const gameId = this.getGameId(block)
          const isGameCompleted = this.getGameCompletionStatus(block)
          console.log(`PageContent: Game ${block.type} (${gameId}) completed: ${isGameCompleted}`)
          if (!isGameCompleted) {
            console.log(`PageContent: Form incomplete - game ${block.type} not completed`)
            return false
          }
        }
      }
      
      console.log('PageContent: Form complete for page')
      return true
    }
  },
  watch: {
    pageId: {
      handler(newPageId, oldPageId) {
        if (newPageId && newPageId !== oldPageId) {
          // Load game completions from persistent storage when page changes
          this.loadGameCompletions()
          this.loadContent()
        }
      },
      immediate: true
    },
    
    pageContent: {
      handler(newContent) {
        if (newContent && newContent.blocks) {
          console.log('PageContent loaded:', {
            pageId: this.pageId,
            blocksCount: newContent.blocks.length,
            quizBlocks: newContent.blocks.filter(b => b.type === 'form_radio_input').length,
            blocks: newContent.blocks
          })
          
          // Log quiz question blocks specifically
          newContent.blocks.forEach(block => {
            if (block.type === 'form_radio_input') {
              this.logBlockData(block)
            }
          })
        }
      },
      immediate: true
    },
    
    // Watch form changes to debug validation
    form: {
      handler(newForm) {
        console.log('PageContent: Form changed:', {
          quizAnswers: newForm.quiz_answers,
          isPageComplete: this.localIsPageComplete
        })
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // Get current language from i18n (Options API style)
    getCurrentLanguage() {
      return this.$i18n?.locale || 'en'
    },
    
    loadGameCompletions() {
      // Load game completions from persistent storage
      const formCompletions = (this.form && this.form.game_completions) ? this.form.game_completions : {}
      if (moduleManager.isModuleActive('game')) {
        const persistedCompletions = gameModule.getAllGameCompletions()
        console.log('PageContent: Loading game completions from persistent storage:', persistedCompletions)
        this.gameCompletions = { ...formCompletions, ...persistedCompletions }
      } else {
        // Reset if game module is not active
        this.gameCompletions = { ...formCompletions }
      }
    },
    
    async loadContent() {
      if (!this.pageId) return
      
      this.loading = true
      try {
        const language = this.getCurrentLanguage()
        console.log("Loading content for page:", this.pageId, "with tour:", this.currentTourId, "language:", language)
        
        const response = await api.getContent(this.pageId, language, this.currentTourId)
        console.log("Content API response:", response.data)
        console.log("Content API response structure:", {
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          hasBlocks: response.data?.blocks ? true : false,
          blocksLength: response.data?.blocks?.length || 0,
          blocks: response.data?.blocks || []
        })
        
        this.pageContent = response.data
        
        // Load game completions from persistent storage after content is loaded
        this.loadGameCompletions()
        
        // Emit the page content to the parent component
        this.$emit('page-content-loaded', {
          pageId: this.pageId,
          content: this.pageContent
        })
        
        // Execute page actions after content is loaded
        this.executePageActions(this.pageId, this.pageContent)
        
      } catch (error) {
        console.error('Error loading page content:', error)
        this.pageContent = null
      } finally {
        this.loading = false
      }
    },
    
    handleFormSubmit(event) {
      console.log('Form submitted in PageContent:', event)
      
      // Ensure game completions are included in form data
      const formData = {
        ...this.form,
        game_completions: this.gameCompletions
      }
      
      // Create a synthetic event object with form data
      const syntheticEvent = {
        target: {
          name: 'form_submit_button',
          value: formData
        },
        formData: formData,
        message: this.currentMessage
      }
      
      // Emit the submit event to the parent component
      if (this.handleSubmit) {
        this.handleSubmit(syntheticEvent)
      } else {
        // Fallback: emit a custom event
        this.$emit('form-submit', syntheticEvent)
      }
    },
    handleJigsawComplete(event) {
      this.handleGameComplete(event, 'game_jigsaw_puzzle')
    },
    
    handleSpotterComplete(event) {
      this.handleGameComplete(event, 'game_image_spotter')
    },
    
    handleEraserComplete(event) {
      this.handleGameComplete(event, 'game_image_eraser')
    },
    
    handleSweeperComplete(event) {
      this.handleGameComplete(event, 'game_image_sweeper')
    },
    
    handleGameComplete(event, gameType) {
      console.log('Game complete in PageContent:', event, 'Game type:', gameType)
      
      // Find the game block that was completed by checking the game type
      const gameBlock = this.pageContent?.blocks?.find(block => {
        if (!this.isGameBlock(block.type)) return false
        return block.type === gameType
      })
      
      if (gameBlock) {
        const gameId = this.getGameId(gameBlock)
        console.log(`Marking game ${gameBlock.type} (${gameId}) as completed`)

        // For image spotter, store correctness answer in form state for navigation
        if (gameType === 'game_image_spotter') {
          if (!this.form.game_answers) this.form.game_answers = {}
          const answer = event && typeof event === 'object' && 'answer' in event ? event.answer : (event === true ? 'correct' : 'incorrect')
          this.form.game_answers = { ...this.form.game_answers, [gameId]: answer }
          console.log('PageContent: handleGameComplete: form.game_answers', this.form.game_answers)
        } else {
          // if other than game image spotter, store as completed
          this.storeGameCompletion(gameId)
        }
        
        console.log('Game completions updated:', this.gameCompletions)
        console.log('Form game completions updated:', this.form.game_completions)
        
        // Execute game completion actions if they exist
        this.executeGameCompletionActions(gameBlock, gameId)
      } else {
        console.warn('Could not identify which game was completed for type:', gameType)
      }
      
      // Emit the event with game type info
      this.$emit('game-complete', { event, gameType, gameId: gameBlock ? this.getGameId(gameBlock) : null })
    },

    storeGameCompletion(gameId) {
      this.gameCompletions = { ...this.gameCompletions, [gameId]: true }
      gameModule.setGameCompletion(gameId, true)
      if (!this.form.game_completions) {
        this.form.game_completions = {}
      }
      this.form.game_completions = { ...this.form.game_completions, [gameId]: true }
    },
    
    isGameBlock(blockType) {
      return [
        'game_jigsaw_puzzle',
        'game_image_spotter', 
        'game_image_eraser',
        'game_image_sweeper'
      ].includes(blockType)
    },
    
    getGameId(block) {
      // Use block ID if available, otherwise use a combination of type and index
      return block.id || `${block.type}_${this.pageContent.blocks.indexOf(block)}`
    },
    
    getGameCompletionStatus(block) {
      const gameId = this.getGameId(block)
      // Check both local state and persistent storage

      console.log('getGameCompletionStatus: gameId:', gameId)

      const formCompletion = this.form?.game_completions ? this.form.game_completions[gameId] : false
      return this.gameCompletions[gameId] || formCompletion || false
    },
    
    logBlockData(block) {
      if (block.type === 'form_radio_input') {
        console.log('Form field block data:', {
          blockId: block.id,
          blockType: block.type,
          dataExists: !!block.data,
          dataKeys: block.data ? Object.keys(block.data) : [],
          hasOptions: block.data?.options ? true : false,
          hasChoices: block.data?.choices ? true : false,
          hasAnswers: block.data?.answers ? true : false,
          optionsCount: block.data?.options?.length || 0,
          choicesCount: block.data?.choices?.length || 0,
          answersCount: block.data?.answers?.length || 0,
          fullBlock: block
        })
      }
    },
    
    getTextAnswer(questionId) {
      if (!questionId) return ''
      
      const questionIdStr = questionId.toString()
      
      // First check local form state
      if (this.form.quiz_answers && this.form.quiz_answers[questionIdStr]) {
        return this.form.quiz_answers[questionIdStr]
      }
      
      // Then check via getQuizAnswer function if available
      if (this.getQuizAnswer) {
        return this.getQuizAnswer({ type: 'form_text_input', data: { id: questionId } }) || ''
      }
      
      return ''
    },
    
    getVariableValueFromForm(variableSlug) {
      if (!variableSlug) return ''
      
      // Get value from GameModule (persistent storage)
      const value = gameModule.getVariableValue(variableSlug)
      if (value !== null && value !== undefined) {
        return value
      }
      
      return ''
    },

    // Execute game completion actions when a game is completed
    executeGameCompletionActions(gameBlock, gameId) {
      console.log('=== EXECUTE GAME COMPLETION ACTIONS ===')
      console.log('Game block:', gameBlock)
      console.log('Game ID:', gameId)

      // Check if GameModule is active
      if (!moduleManager.isModuleActive('game')) {
        console.log('GameModule is not active, skipping game completion actions')
        return
      }

      // Get actions from the game block data
      const actions = gameBlock?.data?.actions || gameBlock?.actions || []
      console.log('Game completion actions found:', actions)

      if (actions.length === 0) {
        console.log('No game completion actions found for game', gameId)
        return
      }

      console.log('Executing game completion actions...')

      // Execute each action
      actions.forEach((action, index) => {
        console.log(`Executing game completion action ${index + 1}:`, action)
        this.executeGameAction(action, gameId, index)
      })
    },

    // Execute a single game completion action
    executeGameAction(action, gameId, actionIndex) {
      console.log('Executing game action:', action)

      // Create a unique key for this action to track if it's been executed
      const actionKey = `game_${gameId}_action_${actionIndex}`
      console.log('Game action key:', actionKey)

      // Get trigger from the action structure
      const actionData = action.data || action
      const trigger = actionData.trigger
      console.log('Game action trigger:', trigger)

      // Check if action should be triggered
      let shouldExecute = false

      if (!trigger) {
        // No trigger specified - execute
        shouldExecute = true
        console.log('No trigger specified - will execute')
      } else if (trigger === 'every_time') {
        // Execute every time
        shouldExecute = true
        console.log('Trigger is every_time - will execute')
      } else if (trigger === 'first_time') {
        // Execute only the first time - check if already executed using GameModule
        shouldExecute = !gameModule.isActionExecuted(actionKey)
        console.log('Trigger is first_time - should execute:', shouldExecute)
      }

      if (shouldExecute) {
        console.log(`Executing game action (trigger: ${trigger || 'default'})`)

        if (typeof action === 'object' && action.target_type) {
          // API action structure
          if (action.target_type === 'variable') {

            gameModule.executeVariableAction(action, this.pointData?.point?.id)
          } else if (action.target_type === 'object') {
            gameModule.executeObjectAction(action, this.pointData?.point?.id)
          } else if (action.target_type === 'task') {
            gameModule.executeTaskAction(action, this.pointData?.point?.id)
          } else {
            console.warn(`Unknown game action type: ${action.target_type}`)
          }
        } else {
          console.warn(`Unknown game action format:`, action)
        }

        // Mark action as executed if it's a 'first_time' trigger using GameModule
        if (trigger === 'first_time') {
          gameModule.markActionExecuted(actionKey)
          console.log(`Marked game action as executed`)
        }
      } else {
        console.log(`Skipping game action (trigger: ${trigger})`)
      }
    },

    // Execute page actions when page content is loaded
    executePageActions(pageId, pageData) {
      console.log('=== EXECUTE PAGE ACTIONS IN PAGE CONTENT ===')
      console.log('Page ID:', pageId)
      console.log('Page data:', pageData)
      
      // Check if GameModule is active
      if (!moduleManager.isModuleActive('game')) {
        console.log('GameModule is not active, skipping page actions')
        return
      }
      
      // Get actions from the page data
      const actions = pageData?.actions || []
      console.log('Page actions found:', actions)
      
      if (actions.length === 0) {
        console.log('No page-specific actions found for page', pageId)
        return
      }
      
      console.log('Executing page actions...')
      
      // Use GameModule to execute page actions
      gameModule.executePageActions(pageId, pageData)
    }
  }
}
</script>